import { useCallback, useState } from "react";

const STORAGE_PREFIX = "dd:shared:";

const keyFor = (pollId: string) => `${STORAGE_PREFIX}${pollId}`;

function readSharedSet(pollId: string): Set<string> {
  try {
    const raw = localStorage.getItem(keyFor(pollId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.map(String)) : new Set();
  } catch {
    return new Set();
  }
}

function writeSharedSet(pollId: string, set: Set<string>): void {
  try {
    localStorage.setItem(keyFor(pollId), JSON.stringify([...set]));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

export interface UseShareTrackingOptions {
  pollId: string | undefined;
}

export interface UseShareTrackingReturn {
  /** True if the organizer has already pressed "Copy link" for this participant. */
  isShared: (participantId: string) => boolean;
  /** Mark a participant as shared (persisted to localStorage). */
  markShared: (participantId: string) => void;
  /** Clear all shared marks for this poll. */
  reset: () => void;
}

/**
 * Tracks per-poll which pending participants the organizer has already
 * "shared the link with", purely as a UI memory aid. Persisted in
 * localStorage so reload preserves the marks. No network, no email.
 */
export function useShareTracking({
  pollId,
}: UseShareTrackingOptions): UseShareTrackingReturn {
  const [shared, setShared] = useState<Set<string>>(() =>
    pollId ? readSharedSet(pollId) : new Set(),
  );

  const isShared = useCallback(
    (participantId: string) => shared.has(participantId),
    [shared],
  );

  const markShared = useCallback(
    (participantId: string) => {
      if (!pollId) return;
      setShared((prev) => {
        if (prev.has(participantId)) return prev;
        const next = new Set(prev);
        next.add(participantId);
        writeSharedSet(pollId, next);
        return next;
      });
    },
    [pollId],
  );

  const reset = useCallback(() => {
    if (!pollId) return;
    setShared(new Set());
    try {
      localStorage.removeItem(keyFor(pollId));
    } catch {
      /* non-fatal */
    }
  }, [pollId]);

  return { isShared, markShared, reset };
}
